import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Row, Spinner, Table } from 'react-bootstrap'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { LEAVE_STATUS, TRAINING_TYPES, fmtDateTH, fmtPeriodRange, toBE } from '../hr/hrShared'

// มุมมองสำหรับหัวหน้าวิชาการสามัญ (อ่านอย่างเดียว) — ครูที่แจ้งไปอบรม/ประชุม
// เพื่อวางแผนการสอนแทน; กดอนุมัติได้ที่หน้า /hr/training-approval

// การ์ด 1 รายการ สำหรับมุมมองมือถือ
const TrainingCard = ({ r }) => {
  const ls = LEAVE_STATUS[r.tr_status] || LEAVE_STATUS.pending
  const tt = TRAINING_TYPES[r.tr_type] || TRAINING_TYPES.training
  return (
    <div className="border rounded p-3">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold text-truncate">{r.t_name}</div>
          <div className="fs-xs text-muted">{r.t_idnumber}</div>
        </div>
        <div className="text-end">
          <Badge bg={tt.bg} className="me-1">{tt.label}</Badge>
          <Badge bg={ls.bg}>{ls.label}</Badge>
        </div>
      </div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">หัวข้อ:</span> {r.tr_title}</div>
        <div><span className="text-muted">วันที่:</span> {fmtDateTH(r.tr_start_date)} - {fmtDateTH(r.tr_end_date)} ({r.tr_days} วัน)</div>
        <div><span className="text-muted">ช่วงเวลา:</span> {fmtPeriodRange(r.tr_period_start, r.tr_period_end) || 'ทั้งวัน'}</div>
        {r.tr_organizer && <div><span className="text-muted">หน่วยงานผู้จัด:</span> {r.tr_organizer}</div>}
        {r.tr_location && <div><span className="text-muted">สถานที่:</span> {r.tr_location}</div>}
      </div>
    </div>
  )
}

export default function TeachersOnTraining() {
  const currentYear = new Date().getFullYear()
  const today = new Date().toLocaleDateString('en-CA')
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('approved')
  const [type, setType] = useState('')
  const [date, setDate] = useState('')
  const [year, setYear] = useState(String(currentYear))
  const [keyword, setKeyword] = useState('')
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const fetchRequests = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      if (type) params.set('type', type)
      if (date) params.set('date', date)
      else if (year) params.set('year', year)
      if (keyword) params.set('keyword', keyword)
      const res = await axiosInstance.get(`/api/hr/training-requests?${params}`)
      if (res.data.success) setRequests(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [status, type, date, year, keyword])

  useEffect(() => {
    const timer = setTimeout(fetchRequests, 300)
    return () => clearTimeout(timer)
  }, [fetchRequests])

  // ครูที่อยู่ระหว่างอบรม/ประชุมวันนี้ (เฉพาะรายการที่อนุมัติแล้ว)
  const onTrainingToday = useMemo(() =>
    requests.filter(r => r.tr_status === 'approved' && r.tr_start_date <= today && today <= r.tr_end_date),
    [requests, today])

  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2]

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-presentation-line me-2 text-primary" />
              ครูไปอบรม / ประชุม / กิจกรรม
            </h4>
          </div>

          <Alert variant="light" className="border fs-sm py-2">
            <i className="ri-information-line me-1" />
            รายการแจ้งไปอบรม/ประชุม/กิจกรรมของครู — ใช้วางแผนการจัดสอนแทน
            (กดอนุมัติได้ที่เมนู <b>อนุมัติไปอบรม/ประชุม/กิจกรรม</b>)
            {onTrainingToday.length > 0 && (
              <span className="ms-2 fw-semibold text-primary">
                วันนี้มีครูไปอบรม/ประชุม/กิจกรรม {onTrainingToday.length} คน:{' '}
                {onTrainingToday.map(r => r.t_name).join(', ')}
              </span>
            )}
          </Alert>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one mb-3">
            <Card.Body className="p-3">
              <Row className="g-2">
                <Col xs={12} md={3}>
                  <InputGroup size="sm">
                    <InputGroup.Text><i className="ri-search-line" /></InputGroup.Text>
                    <Form.Control placeholder="ค้นหาชื่อ รหัส หรือหัวข้อ" value={keyword}
                      onChange={e => setKeyword(e.target.value)} />
                  </InputGroup>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Select size="sm" value={type} onChange={e => setType(e.target.value)}>
                    <option value="">ทุกประเภท</option>
                    {Object.entries(TRAINING_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </Form.Select>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Select size="sm" value={status} onChange={e => setStatus(e.target.value)}>
                    <option value="approved">อนุมัติแล้ว</option>
                    <option value="pending">รออนุมัติ</option>
                    <option value="">ทุกสถานะ</option>
                  </Form.Select>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Control size="sm" type="date" value={date} title="กรองตามวันที่"
                    onChange={e => setDate(e.target.value)} />
                </Col>
                <Col xs={6} md={1}>
                  <Form.Select size="sm" value={year} disabled={!!date} onChange={e => setYear(e.target.value)}>
                    {yearOptions.map(y => <option key={y} value={y}>พ.ศ. {toBE(y)}</option>)}
                  </Form.Select>
                </Col>
                <Col xs={12} md={2}>
                  <Button size="sm" variant="outline-secondary" className="w-100"
                    onClick={() => { setKeyword(''); setType(''); setStatus('approved'); setDate(''); setYear(String(currentYear)) }}>
                    <i className="ri-refresh-line me-1" />ล้างตัวกรอง
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center justify-content-between gap-2">
              <Card.Title as="h6" className="mb-0">รายการครูไปอบรม/ประชุม/กิจกรรม</Card.Title>
              <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : requests.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่พบรายการครูไปอบรม/ประชุม/กิจกรรม</div>
              ) : view === 'card' ? (
                <div className="p-3">
                  <Row className="g-3">
                    {requests.map(r => (
                      <Col key={r.tr_id} xs={12} md={6} xl={4}>
                        <TrainingCard r={r} />
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th>ชื่อ-สกุล</th>
                      <th>ประเภท</th>
                      <th>หัวข้อ</th>
                      <th>วันที่</th>
                      <th className="text-end">จำนวนวัน</th>
                      <th>ช่วงคาบ</th>
                      <th>หน่วยงานผู้จัด</th>
                      <th>สถานที่</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map(r => {
                      const ls = LEAVE_STATUS[r.tr_status] || LEAVE_STATUS.pending
                      const tt = TRAINING_TYPES[r.tr_type] || TRAINING_TYPES.training
                      return (
                        <tr key={r.tr_id}>
                          <td>
                            {r.t_name}
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.t_idnumber}</div>
                          </td>
                          <td><Badge bg={tt.bg}>{tt.label}</Badge></td>
                          <td className="text-truncate" style={{ maxWidth: 220 }} title={r.tr_title}>{r.tr_title}</td>
                          <td>{fmtDateTH(r.tr_start_date)} - {fmtDateTH(r.tr_end_date)}</td>
                          <td className="text-end">{r.tr_days}</td>
                          <td className="text-nowrap">{fmtPeriodRange(r.tr_period_start, r.tr_period_end) || <span className="text-muted">ทั้งวัน</span>}</td>
                          <td className="text-truncate" style={{ maxWidth: 150 }} title={r.tr_organizer || ''}>{r.tr_organizer || '-'}</td>
                          <td className="text-truncate" style={{ maxWidth: 140 }} title={r.tr_location || ''}>{r.tr_location || '-'}</td>
                          <td><Badge bg={ls.bg}>{ls.label}</Badge></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />
    </>
  )
}
